import { useCallback, useState } from "react";
import type { AcceptInvite, AgreementSignature } from "../../../../common/src";
import { USER_AGREEMENT, acceptInviteSchema } from "../../../../common/src";
import { useAction } from "../../hooks/useAction";
import { useAsyncData } from "../../hooks/useAsyncData";
import { navigate } from "../../hooks/useRoute";
import { DEFAULT_ROUTE } from "../../routes";
import { useSession } from "../../session/SessionContext";
import { trpc } from "../../trpc";
import { toFieldErrors } from "../../utils/fieldErrors";
import { AgreementSigner } from "../agreements/AgreementSigner";
import { Field } from "../shared/Field";
import { Notice } from "../shared/Notice";
import { LoadError, Loading } from "../shared/StatusViews";
import { useFormStyles } from "../shared/styles";
import { AuthLayout } from "./AuthLayout";
import { NewPasswordFields } from "./NewPasswordFields";

/** Opened from the invitation email: set up the account and sign the User Agreement in one step. */
export const AcceptInvitePage = ({ token }: { token: string }) => {
  const form = useFormStyles();
  const { signedIn } = useSession();
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadPreview = useCallback(
    (signal: AbortSignal) =>
      token
        ? trpc.auth.previewInvite.query({ token }, { signal })
        : Promise.resolve({ valid: false as const }),
    [token]
  );
  const preview = useAsyncData(loadPreview);
  const accept = useAction(
    useCallback(
      (input: AcceptInvite) => trpc.auth.acceptInvite.mutate(input),
      []
    )
  );

  const onSign = async (signature: AgreementSignature) => {
    const parsed = acceptInviteSchema.safeParse({
      ...signature,
      token,
      displayName,
      password,
    });
    const fieldErrors = parsed.success ? {} : toFieldErrors(parsed.error);
    if (password && confirm !== password) {
      fieldErrors.confirm = "Passwords don't match.";
    }
    if (!parsed.success || Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    const user = await accept.run(parsed.data);
    if (user) {
      navigate(DEFAULT_ROUTE);
      signedIn(user);
    }
  };

  if (preview.error) {
    return (
      <AuthLayout width={480}>
        <LoadError message={preview.error} onRetry={preview.reload} />
      </AuthLayout>
    );
  }
  if (!preview.data) {
    return (
      <AuthLayout width={480}>
        <Loading label="Checking your invitation…" />
      </AuthLayout>
    );
  }
  if (!preview.data.valid) {
    return (
      <AuthLayout width={480}>
        <Notice kind="info">
          This invitation link has expired or was already used. Ask the boat
          owner to send a new one, or sign in if you've already joined.
        </Notice>
        <a href="#/">Go to sign in</a>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout width={760}>
      <h2>Join VetBoats</h2>
      <p>
        You're setting up the account for <strong>{preview.data.email}</strong>.
        Choose a password, then read the agreement in full and sign it. We'll
        email you a copy.
      </p>
      <AgreementSigner
        doc={USER_AGREEMENT}
        submitLabel="Sign and join VetBoats"
        pending={accept.pending}
        error={
          accept.error ??
          (Object.keys(errors).length > 0
            ? "Please fix the fields above."
            : undefined)
        }
        onSign={onSign}
      >
        {/* Lets password managers save the login under the right email. */}
        <input
          type="email"
          autoComplete="username"
          value={preview.data.email}
          readOnly
          hidden
        />
        <Field
          label="Your name"
          htmlFor="invite-name"
          hint="How you'll appear to the boat owner."
          error={errors.displayName}
        >
          <input
            id="invite-name"
            className={form.control}
            autoComplete="name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </Field>
        <NewPasswordFields
          password={password}
          confirm={confirm}
          onPasswordChange={setPassword}
          onConfirmChange={setConfirm}
          passwordError={errors.password}
          confirmError={errors.confirm}
        />
      </AgreementSigner>
    </AuthLayout>
  );
};
